import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { remoteError, remoteValue } from "../data/remoteState";
import {
  archivedGameToView,
  gameToView,
  playersToView,
  pregameToView,
} from "../data/viewModel";
import type { PlateAppearanceResult } from "../../shared/domain/gameReducer";
import { normalizeApplicationError } from "../shared/errors";
import { useCommandRunner } from "./useCommandRunner";
import { useOptimisticGame } from "./useOptimisticGame";
import { useOptimisticPlayerPreferences } from "./useOptimisticPlayerPreferences";
import { useOptimisticPregame } from "./useOptimisticPregame";
import { useOptimisticSchedule } from "./useOptimisticSchedule";
import { usePlayDraft } from "./usePlayDraft";
import { useSerializedGameQueue } from "./useSerializedGameQueue";
import { useTeamCommands } from "./useTeamCommands";
import { useTeamQueries } from "./useTeamQueries";
import { useTeamUiState } from "./useTeamUiState";
import { useIntentStatus } from "./useIntentStatus";
import type { TeamController, TeamUser } from "./teamTypes";

interface TeamControllerOptions {
  teamId: string;
  user: TeamUser | null | undefined;
}

interface LocalScorerClaim {
  base: string | null;
  name: string | null;
}

interface HalfInningSummary {
  gameId: string | null;
  runs: number;
}

interface PreviousGameState {
  id: string | null;
  batting: boolean | null;
  revision: number | null;
  inning: number | null;
  inningRuns: number;
}

interface PendingBoundaryUndo {
  gameId: string;
  revision: number;
}

interface ScheduleSelectionBridge {
  teamId: string;
  scheduleId: string;
  committedRevision: number;
}

export function useTeamController({
  teamId,
  user,
}: TeamControllerOptions): TeamController {
  const [browserOnline, setBrowserOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine !== false,
  );
  const ui = useTeamUiState();
  const queries = useTeamQueries({
    teamId,
    uid: user?.uid ?? null,
    manageOpen: ui.manageOpen,
  });
  const teamDoc = remoteValue(queries.team);
  const pregameDoc = remoteValue(queries.pregame);
  const playerDocs = remoteValue(queries.players);
  const gameDoc = remoteValue(queries.game);
  const eventDocs = remoteValue(queries.events);
  const archivedDocs = remoteValue(queries.archivedGames);
  const scheduleDocs = remoteValue(queries.scheduleGames);
  const playoffSeriesDocs = remoteValue(queries.playoffSeries);
  const seasonDocs = remoteValue(queries.seasons);
  const inviteDoc = remoteValue(queries.invite);
  const recentEventDocs = remoteValue(queries.recentEvents);
  const activeGameId = teamDoc?.activeGameId ?? null;

  const blockingError =
    remoteError(queries.team) ||
    remoteError(queries.pregame) ||
    remoteError(queries.players) ||
    remoteError(queries.game) ||
    remoteError(queries.events) ||
    remoteError(queries.archivedGames) ||
    remoteError(queries.scheduleGames) ||
    remoteError(queries.playoffSeries) ||
    remoteError(queries.seasons) ||
    null;
  const dataError = blockingError
    ? normalizeApplicationError(blockingError)
    : null;
  const loading =
    queries.team.status === "loading" ||
    queries.pregame.status === "loading" ||
    queries.players.status === "loading" ||
    queries.scheduleGames.status === "loading" ||
    queries.playoffSeries.status === "loading" ||
    queries.seasons.status === "loading" ||
    (!!activeGameId &&
      (queries.game.status === "loading" ||
        queries.events.status === "loading"));

  const { players: authoritativePlayers } = useMemo(
    () => playersToView(playerDocs ?? []),
    [playerDocs],
  );
  const optimisticPreferences = useOptimisticPlayerPreferences({
    scopeKey: teamId,
    players: authoritativePlayers,
  });
  const { players, prefs } = optimisticPreferences;
  const authoritativePregame = useMemo(
    () => pregameToView(pregameDoc),
    [pregameDoc],
  );
  const gameArchive = useMemo(
    () =>
      (archivedDocs ?? [])
        .map(archivedGameToView)
        .sort((left, right) => left.date.localeCompare(right.date)),
    [archivedDocs],
  );
  const authoritativeScheduleGames = useMemo(
    () => (scheduleDocs ?? []).map((entry) => ({ ...entry })),
    [scheduleDocs],
  );
  const [scheduleSelectionBridge, setScheduleSelectionBridge] =
    useState<ScheduleSelectionBridge | null>(null);
  const bridgedPregame = useMemo(() => {
    if (!scheduleSelectionBridge || scheduleSelectionBridge.teamId !== teamId) {
      return authoritativePregame;
    }
    const selected = authoritativeScheduleGames.find(
      (entry) => entry.id === scheduleSelectionBridge.scheduleId,
    );
    if (!selected) return authoritativePregame;
    return {
      ...authoritativePregame,
      selectedScheduleId: selected.id,
      oppName: selected.opponentName,
      isHome: selected.isHome,
      scheduledDate: selected.scheduledDate,
      timeSlot: selected.timeSlot,
      field: selected.field,
      beerDuty: selected.beerDuty,
      revision: Math.max(
        authoritativePregame.revision,
        scheduleSelectionBridge.committedRevision,
      ),
    };
  }, [
    authoritativePregame,
    authoritativeScheduleGames,
    scheduleSelectionBridge,
    teamId,
  ]);
  const playoffSeries = useMemo(
    () => (playoffSeriesDocs ?? []).map((series) => ({ ...series })),
    [playoffSeriesDocs],
  );
  const seasons = useMemo(
    () =>
      [...(seasonDocs ?? [])].sort(
        (left, right) =>
          (right.year ?? Number.NEGATIVE_INFINITY) -
            (left.year ?? Number.NEGATIVE_INFINITY) ||
          right.label.localeCompare(left.label),
      ),
    [seasonDocs],
  );
  const activity = useMemo(
    () =>
      (recentEventDocs ?? [])
        .filter((event) => event.category !== "game")
        .slice(0, 12),
    [recentEventDocs],
  );
  const invite = useMemo(
    () => ({
      loading: queries.invite.status === "loading",
      token: inviteDoc?.token ?? null,
      active: inviteDoc?.active === true,
      version: inviteDoc?.version ?? null,
    }),
    [inviteDoc, queries.invite.status],
  );
  const presence = remoteValue(queries.presence) ?? [];

  const optimisticPregame = useOptimisticPregame({
    scopeKey: teamId,
    pregame: bridgedPregame,
    players,
    serverJerseyHolderPlayerId: teamDoc?.jerseyHolderPlayerId ?? null,
    teamUpdatedAt: teamDoc?.updatedAt,
    pregameUpdatedAt: pregameDoc?.updatedAt,
  });
  const optimisticGame = useOptimisticGame({
    scopeKey: `${teamId}:${activeGameId ?? ""}`,
    gameDoc,
    eventDocs,
    serverJerseyHolderPlayerId: teamDoc?.jerseyHolderPlayerId ?? null,
    baseJerseyHolderPlayerId: optimisticPregame.jerseyHolderPlayerId,
    teamUpdatedAt: teamDoc?.updatedAt,
  });
  const optimisticSchedule = useOptimisticSchedule({
    scopeKey: teamId,
    scheduleGames: authoritativeScheduleGames,
    authoritativePregame,
    authoritativePregameUpdatedAt: pregameDoc?.updatedAt,
    renderedPregame: optimisticPregame.pregame,
    authoritativeGame: gameDoc,
    renderedGame: optimisticGame.rawGame,
    activeGameId,
  });
  const scheduleGames = optimisticSchedule.scheduleGames;
  const pregame = optimisticSchedule.pregame;
  const rawGame = optimisticSchedule.rawGame;
  const game = useMemo(() => gameToView(rawGame), [rawGame]);
  const jerseyHolderPlayerId = optimisticGame.jerseyHolderPlayerId;
  const activePlayers = useMemo(
    () => players.filter((player) => pregame.activeIds.includes(player.id)),
    [players, pregame],
  );
  const serverScorerUid = gameDoc?.scorerUid ?? null;
  const serverScorerName = gameDoc?.scorerNameSnapshot ?? null;
  const [localScorerClaim, setLocalScorerClaim] =
    useState<LocalScorerClaim | null>(null);
  useEffect(() => {
    if (localScorerClaim && serverScorerUid !== localScorerClaim.base) {
      setLocalScorerClaim(null);
    }
  }, [localScorerClaim, serverScorerUid]);
  const isScorer =
    !!user &&
    (localScorerClaim !== null ||
      (serverScorerUid !== null && serverScorerUid === user.uid));
  const scorerName = localScorerClaim
    ? localScorerClaim.name
    : serverScorerName;
  const scoringBlocked = !!game && !isScorer;
  const [halfInningSummary, setHalfInningSummary] =
    useState<HalfInningSummary | null>(null);
  const previousGameRef = useRef<PreviousGameState>({
    id: null,
    batting: null,
    revision: null,
    inning: null,
    inningRuns: 0,
  });
  const pendingBoundaryUndoRef = useRef<PendingBoundaryUndo | null>(null);
  const playDraft = usePlayDraft();
  const commandRunner = useCommandRunner({
    showToast: ui.showToast,
  });
  const intents = useIntentStatus();
  const queue = useSerializedGameQueue({
    beginIntent: intents.begin,
  });
  const handleScorerClaimed = useCallback(
    (name: string | null) => {
      setLocalScorerClaim({ base: serverScorerUid, name });
    },
    [serverScorerUid],
  );
  const handleScheduleSelected = useCallback(
    (scheduleId: string, committedRevision: number) => {
      setScheduleSelectionBridge({ teamId, scheduleId, committedRevision });
    },
    [teamId],
  );
  const commands = useTeamCommands({
    teamId,
    players,
    prefs,
    activePlayers,
    pregame,
    game,
    gameArchive,
    scheduleGames,
    playoffSeries,
    seasons,
    activeSeasonId: teamDoc?.activeSeasonId ?? null,
    rawGame,
    playDraft,
    run: commandRunner.run,
    enqueue: queue.enqueue,
    projectGame: optimisticGame.project,
    projectUndo: optimisticGame.projectUndo,
    markGameCommitted: optimisticGame.markCommitted,
    rejectGameProjection: optimisticGame.reject,
    projectPregame: optimisticPregame.project,
    markPregameCommitted: optimisticPregame.markCommitted,
    rejectPregameProjection: optimisticPregame.reject,
    projectPreference: optimisticPreferences.projectToggle,
    preferenceRevision: optimisticPreferences.currentRevision,
    markPreferenceCommitted: optimisticPreferences.markCommitted,
    rejectPreferenceProjection: optimisticPreferences.reject,
    projectSchedule: optimisticSchedule.projectUpdate,
    markScheduleCommitted: optimisticSchedule.markCommitted,
    rejectScheduleProjection: optimisticSchedule.reject,
    schedulePregameBarrier: optimisticSchedule.pregameBarrier,
    showToast: ui.showToast,
    scoringBlocked,
    scorerName,
    onScorerClaimed: handleScorerClaimed,
    onScheduleSelected: handleScheduleSelected,
  });
  const { setScreen } = ui;
  const { beginResult, clearPendingPlay, resetPlayDraft } = playDraft;
  const { recordPlay, undo: runUndo } = commands;
  const undo = useCallback(async (): Promise<boolean> => {
    const currentGame = game;
    const pendingBoundaryUndo =
      currentGame && optimisticGame.undoCrossesHalfInning
        ? { gameId: currentGame.id, revision: currentGame.revision }
        : null;
    pendingBoundaryUndoRef.current = pendingBoundaryUndo;
    const succeeded = await runUndo();
    if (!succeeded && pendingBoundaryUndoRef.current === pendingBoundaryUndo) {
      pendingBoundaryUndoRef.current = null;
    }
    return succeeded;
  }, [game, optimisticGame.undoCrossesHalfInning, runUndo]);
  const confirmHalfInningTransition = useCallback(() => {
    setHalfInningSummary(null);
  }, []);
  const serverBusy =
    commandRunner.pendingCount > 0 ||
    queue.queueDepth > 0 ||
    intents.view.pendingCount > 0;
  const connectionState = !browserOnline
    ? "offline"
    : intents.view.unknownCount > 0
      ? "checking"
      : commandRunner.lastErrorCategory === "stale-revision"
        ? "conflict"
        : serverBusy
          ? "saving"
          : "live";

  useEffect(() => {
    if (scheduleSelectionBridge?.teamId !== teamId) {
      if (scheduleSelectionBridge) setScheduleSelectionBridge(null);
      return;
    }
    if (
      !scheduleSelectionBridge ||
      !pregameDoc ||
      pregameDoc.revision < scheduleSelectionBridge.committedRevision
    ) {
      return;
    }
    if (pregameDoc.selectedScheduleId !== scheduleSelectionBridge.scheduleId) {
      ui.showToast("The selected game changed on another device");
    }
    setScheduleSelectionBridge(null);
  }, [pregameDoc, scheduleSelectionBridge, teamId, ui]);

  useEffect(() => {
    setScreen(
      activeGameId ? "game" : teamDoc?.activeSeasonId ? "setup" : "season",
    );
    resetPlayDraft();
  }, [activeGameId, teamDoc?.activeSeasonId, setScreen, resetPlayDraft]);

  useEffect(() => {
    const currentGame = game;
    const current: PreviousGameState = {
      id: currentGame?.id ?? null,
      batting: currentGame?.batting ?? null,
      revision: currentGame?.revision ?? null,
      inning: currentGame?.inning ?? null,
      inningRuns: currentGame
        ? (currentGame.ourRunsByInning[currentGame.inning - 1] ?? 0)
        : 0,
    };
    const previous = previousGameRef.current;
    const pendingBoundaryUndo = pendingBoundaryUndoRef.current;
    const appliedBoundaryUndo =
      !!pendingBoundaryUndo &&
      pendingBoundaryUndo.gameId === current.id &&
      current.revision !== null &&
      current.revision > pendingBoundaryUndo.revision;
    if (
      pendingBoundaryUndo &&
      (current.id !== pendingBoundaryUndo.gameId || appliedBoundaryUndo)
    ) {
      pendingBoundaryUndoRef.current = null;
    }
    const endedOurHalf =
      previous.id === current.id &&
      previous.batting === true &&
      current.batting === false;
    previousGameRef.current = current;
    if (previous.id !== current.id || current.batting === true) {
      setHalfInningSummary(null);
    }
    if (!endedOurHalf || appliedBoundaryUndo || previous.inning === null) {
      return;
    }
    const completedInningIndex = previous.inning - 1;
    const completedInningRuns =
      currentGame?.ourRunsByInning[completedInningIndex] ?? previous.inningRuns;
    setHalfInningSummary({ gameId: current.id, runs: completedInningRuns });
  }, [game]);

  useEffect(() => {
    const markOnline = () => setBrowserOnline(true);
    const markOffline = () => setBrowserOnline(false);
    window.addEventListener("online", markOnline);
    window.addEventListener("offline", markOffline);
    return () => {
      window.removeEventListener("online", markOnline);
      window.removeEventListener("offline", markOffline);
    };
  }, []);

  const selectResult = useCallback(
    (result: PlateAppearanceResult): Promise<boolean> => {
      if (!game?.batting || !game.lineup.length) {
        return Promise.resolve(false);
      }
      if (result.key === "K") {
        return recordPlay(result, {}, 0);
      }
      if (!game.bases.some(Boolean)) {
        if (result.key === "FC") return Promise.resolve(false);
        return recordPlay(result, {}, result.key === "HR" ? 1 : 0);
      }
      if (result.key === "FC") {
        const occupied = game.bases
          .map((playerId, baseIndex) => (playerId ? baseIndex : -1))
          .filter((baseIndex) => baseIndex >= 0);
        const occupiedBase = occupied[0];
        if (occupied.length === 1 && occupiedBase !== undefined) {
          return recordPlay(result, { [occupiedBase]: "X" }, 0);
        }
      }
      beginResult(result, game.bases);
      return Promise.resolve(true);
    },
    [game, recordPlay, beginResult],
  );
  const submitPlay = useCallback(
    async (
      result: PlateAppearanceResult,
      decisions: Parameters<typeof recordPlay>[1],
      rbi: number,
    ): Promise<boolean> => {
      const succeeded = await recordPlay(result, decisions, rbi);
      if (succeeded) clearPendingPlay();
      return succeeded;
    },
    [recordPlay, clearPendingPlay],
  );

  return {
    data: {
      loading,
      dataError,
      teamName: teamDoc?.name ?? "",
      activeSeasonId: teamDoc?.activeSeasonId ?? null,
      jerseyHolderPlayerId,
      players,
      prefs,
      activePlayers,
      pregame,
      game,
      authoritativeGameRevision: gameDoc?.revision ?? null,
      log: optimisticGame.log,
      gameArchive,
      scheduleGames,
      playoffSeries,
      seasons,
      activity,
      activityLoading:
        ui.manageOpen && queries.recentEvents.status === "loading",
      presence,
      invite,
    },
    status: {
      canUndo: (game?.undoStack.length ?? 0) > 0,
      undoCrossesHalfInning: optimisticGame.undoCrossesHalfInning,
      canScore: !!game,
      halfInningTransition: halfInningSummary !== null,
      completedHalfInningRuns: halfInningSummary?.runs ?? 0,
      isScorer,
      scorerName,
      scoringClaimed: localScorerClaim !== null || serverScorerUid !== null,
      serverBusy,
      connectionState,
      intentStatus: intents.view,
      trackIntent: intents.track,
    },
    ui: {
      ...ui,
      pendingPlay: playDraft.pendingPlay,
      setPendingPlay: playDraft.setPendingPlay,
      pendingRBI: playDraft.pendingRBI,
    },
    actions: {
      ...commands,
      undo,
      confirmHalfInningTransition,
      selectResult,
      setDecision: playDraft.setDecision,
      adjustPendingRbi: playDraft.adjustPendingRbi,
      submitPlay,
      cancelPlay: playDraft.clearPendingPlay,
    },
  };
}
